import React, { useState } from 'react';
import { Plus, Search, User, Phone, MapPin, Briefcase, ShieldCheck, Edit2, Trash2 } from 'lucide-react';
import { Card, Button, Input, Badge, Select } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const StaffList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const { success, error: toastError } = useToast();
    
    const canCreate = hasPermission('staff', 'create');
    const canUpdate = hasPermission('staff', 'update');
    const canDelete = hasPermission('staff', 'delete');

    const { data: staffResponse, loading, refresh } = useGet(`/staff-members?page=${currentPage}&search=${searchTerm}`);
    
    const staffMembers = staffResponse?.data || [];
    const pagination = staffResponse?.meta || null;

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الموظف؟')) {
            try {
                await api.delete(`/staff-members/${id}`);
                success('تم حذف الموظف بنجاح');
                refresh();
            } catch (err) {
                toastError(err.response?.data?.message || 'خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-6 text-right" dir="rtl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800 flex items-center gap-2">
                        <ShieldCheck className="w-6 h-6 text-primary" />
                        إدارة الموظفين / المستخدمين
                    </h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">إدارة بيانات الموظفين وصفتهم في الجمعية</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {canCreate && (
                        <Button onClick={() => { setEditingStaff(null); setIsModalOpen(true); }} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" />
                            إضافة موظف جديد
                        </Button>
                    )}
                </div>
            </div>

            <Card className="p-4 md:p-6 bg-white/50 backdrop-blur-sm border-slate-100">
                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 w-full">
                        <Input 
                            placeholder="بحث بالاسم، المهنة أو الصفة..." 
                            icon={Search} 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            </Card>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right font-arabic">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">الموظف</th>
                            <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">الصفة</th>
                            <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">المهنة</th>
                            <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">الهاتف</th>
                            <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-20">الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="5" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                        ) : staffMembers?.length === 0 ? (
                            <tr><td colSpan="5" className="py-12 text-center text-slate-400">لا يوجد موظفون مسجلون</td></tr>
                        ) : (
                            staffMembers?.map(member => (
                                <tr key={member.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400"><User size={14} /></div>
                                            <div className="flex flex-col">
                                                <span className="text-sm text-slate-800 font-bold">{member.full_name}</span>
                                                <span className="text-[10px] text-slate-400">{member.address || 'بدون عنوان'}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="primary" className="font-bold">{member.role}</Badge>
                                    </td>
                                    <td className="px-6 py-4 text-xs text-slate-600">
                                        {member.profession || '--'}
                                    </td>
                                    <td className="px-6 py-4 text-xs font-mono text-slate-500" dir="ltr">
                                        {member.phone || '--'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center gap-1">
                                            {canUpdate && (
                                                <button 
                                                    onClick={() => { setEditingStaff(member); setIsModalOpen(true); }}
                                                    className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                                                    title="تعديل"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button 
                                                    onClick={() => handleDelete(member.id)}
                                                    className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
                {/* Pagination */}
                {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500 font-normal">
                            عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> موظف
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="h-8 px-3 text-xs"
                            >السابق</Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                disabled={currentPage === pagination.last_page}
                                className="h-8 px-3 text-xs"
                            >التالي</Button>
                        </div>
                    </div>
                )}
            </Card>

            <Modal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingStaff(null); }}
                title={editingStaff ? "تعديل بيانات الموظف" : "إضافة موظف جديد"}
                size="xl"
            >
                <StaffForm 
                    initialData={editingStaff} 
                    onSuccess={() => { setIsModalOpen(false); setEditingStaff(null); refresh(); }} 
                />
            </Modal>
        </div>
    );
};

const StaffForm = ({ onSuccess, initialData }) => {
    const { success, error: toastError } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        full_name: initialData?.full_name || '',
        address: initialData?.address || '',
        phone: initialData?.phone || '',
        profession: initialData?.profession || '',
        role: initialData?.role || 'مستخدم'
    });

    const roles = [
        'رئيس جمعية',
        'امين المال اول',
        'امين مال ثاني',
        'عضو',
        'نائب الاول',
        'نائب ثاني',
        'مستخدم',
        'مساعد اداري',
        'كاتب',
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (initialData) {
                await api.put(`/staff-members/${initialData.id}`, formData);
                success('تم تحديث بيانات الموظف بنجاح');
            } else {
                await api.post('/staff-members', formData);
                success('تمت إضافة الموظف بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5 text-right">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input 
                    label="الاسم الكامل" 
                    required 
                    value={formData.full_name}
                    onChange={e => setFormData({...formData, full_name: e.target.value})}
                    icon={User}
                    className="bg-slate-50 border-slate-200"
                />
                <Select
                    label="الصفة / المنصب"
                    required
                    options={roles.map(r => ({ value: r, label: r }))}
                    value={formData.role}
                    onChange={e => setFormData({...formData, role: e.target.value})}
                    className="bg-slate-50 border-slate-200"
                />
                <Input 
                    label="المهنة (اختياري)" 
                    value={formData.profession}
                    onChange={e => setFormData({...formData, profession: e.target.value})}
                    icon={Briefcase}
                    className="bg-slate-50 border-slate-200"
                />
                <Input 
                    label="الهاتف (اختياري)" 
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    icon={Phone}
                    className="bg-slate-50 border-slate-200 text-left"
                    dir="ltr"
                />
            </div>
            <Input 
                label="العنوان (اختياري)" 
                value={formData.address}
                onChange={e => setFormData({...formData, address: e.target.value})}
                icon={MapPin}
                className="bg-slate-50 border-slate-200"
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <Button 
                    type="submit" 
                    className="shadow-lg shadow-primary/20 px-10" 
                    isLoading={submitting}
                >
                    {initialData ? 'تحديث البيانات' : 'حفظ الموظف'}
                </Button>
            </div>
        </form>
    );
};

export default StaffList;
